import StaticPopupNew from 'components/StaticPopupNew';
import { getFormattedUnit } from 'config';
import React, { useEffect, useRef, useState } from 'react';
import ReactHtmlParser from "react-html-parser";
import { findQuotationLineItem } from './quotationLineHelpers';

const LabourRFQTable = ({ rfqData, sortedSuppliers, withGst, userProfile, closeRfqForm, openDropdown, handleMaterialDropdown, onTotalLowestLabourCostChange, selectedRatesTab, isEditRatesLocked, selectedMaterialsMap, setSelectedMaterialsMap, showSelectedValueColumn = false, canSelectRates = false, hasBudgetColumns = true, hiddenLineItemKeys = new Set(), getLineItemKey, renderLineItemHideButton }) => {
    // const [selectedMaterialsMap, setSelectedMaterialsMap] = useState({});
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [openRevisionTrail, setOpenRevisionTrail] = useState(() => new Set());

    const togglePopup = (skill) => {
        setSelectedDetail(skill);
        setIsPopupOpen(true);
    };
    const lowestRates = {};
    let totalLowestPriceSum = 0;
    const vendorCellRefs = useRef({});
    const isCurrentPendingApprover = rfqData?.approvalDetails?.some(
        (approval) =>
            approval?.status === "PENDING" &&
            approval?.approvers?.some(
                (approver) => String(approver?.userId) === String(userProfile?.userId)
            )
    );
    const isLabourSelectionLocked =
        isEditRatesLocked ||
        (rfqData?.status === "PENDING APPROVAL" && !isCurrentPendingApprover);

    const parseNumber = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const normalizeDecimalInput = (value) => {
        const sanitized = String(value || "").replace(/[^\d.]/g, "");
        const [integerPart, decimalPart = ""] = sanitized.split(".");
        return decimalPart !== undefined && sanitized.includes(".")
            ? `${integerPart}.${decimalPart.slice(0, 2)}`
            : integerPart;
    };

    const getLabourQuantity = (labourItem) => parseNumber(labourItem?.quantity);

    const getSelectedQuantity = (entry, labourItem) => {
        const quantity = entry?.selectedQuantity !== undefined && entry?.selectedQuantity !== null
            ? entry.selectedQuantity
            : getLabourQuantity(labourItem);
        return parseNumber(quantity);
    };

    const getSelectedQuantityForLabour = (labourId, excludeSupplierId, sourceMap = selectedMaterialsMap) =>
        Object.entries(sourceMap || {}).reduce((sum, [supplierId, labourItems]) => {
            if (excludeSupplierId && String(supplierId) === String(excludeSupplierId)) return sum;
            const selectedLabour = (labourItems || []).find(item => String(item.labourId) === String(labourId));
            if (!selectedLabour) return sum;
            return sum + getSelectedQuantity(selectedLabour, rfqData?.labourDetails?.find(item => String(item?._id) === String(labourId)));
        }, 0);

    const calculateSelectedSupplierPrice = (rate, selectedQuantity) =>
        parseNumber(rate) * parseNumber(selectedQuantity);

    const findVendorLabourItem = (labourItems, labourItem, rowIndex) =>
        findQuotationLineItem(labourItems, labourItem, rowIndex, {
            rfqTextFields: ["skill", "name"],
            quoteTextFields: ["skill", "name"],
        });

    const getRevisionRemarkText = (value) =>
        String(value || "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim();

    const formatRevisionAmount = (value) =>
        new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0);

    const formatRevisionPercent = (value) =>
        `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0)}%`;

    const formatRevisionDifference = (difference, differencePercent) => {
        if (difference === null || difference === undefined) return "-";

        const numericDifference = Number(difference);
        if (!Number.isFinite(numericDifference)) return "-";

        return (
            <>
                {formatRevisionAmount(Math.abs(numericDifference))}{" "}
                {numericDifference < 0 && "\u25BC"}
                {numericDifference > 0 && "\u25B2"}
                {differencePercent !== null && differencePercent !== undefined && (
                    <span className="ml-1 text-[10px]">({Number(differencePercent).toFixed(2)}%)</span>
                )}
            </>
        );
    };

    const formatRevisionDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
    };

    const getLabourQuotedValue = (item) =>
        withGst
            ? parseNumber(item?.totalAmount)
            : parseNumber(item?.priceWithoutGst ?? item?.totalExclGst);

    const getLabourRevisionSignature = (item, labourItem) =>
        JSON.stringify({
            labourRate: parseNumber(item?.labourRate),
            quantity: parseNumber(item?.quantity ?? labourItem?.quantity),
            gst: parseNumber(item?.gst),
            quotedValue: getLabourQuotedValue(item),
            isAvailable: item?.isAvailable ?? true,
            workStartDate: item?.workStartDate || "",
            remark: getRevisionRemarkText(item?.remarks ?? item?.remark),
        });

    const getLabourRevisionTrail = (supplier, labourItem, rowIndex) => {
        let previousSignature = null;

        return (supplier?.vendorQuotations?.quotationSets || [])
            .reduce((trail, quotationSet) => {
                const item = findVendorLabourItem(quotationSet?.labourItems, labourItem, rowIndex);
                if (!item) return trail;

                const signature = getLabourRevisionSignature(item, labourItem);
                if (trail.length > 0 && signature === previousSignature) return trail;

                const revisionIndex = trail.length;

                trail.push({
                    label: `R${revisionIndex}`,
                    date: formatRevisionDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
                    submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                    labourRate: parseNumber(item?.labourRate),
                    quantity: parseNumber(item?.quantity ?? labourItem?.quantity),
                    gst: parseNumber(item?.gst),
                    quotedValue: getLabourQuotedValue(item),
                    remark: getRevisionRemarkText(item?.remarks ?? item?.remark),
                });
                previousSignature = signature;

                return trail;
            }, [])
            .map((revision, revisionIndex, trail) => {
                const previousRevision = trail[revisionIndex - 1];
                const quotedValueDifference = previousRevision
                    ? revision.quotedValue - previousRevision.quotedValue
                    : null;
                const quotedValueDifferencePercent = previousRevision && previousRevision.quotedValue !== 0
                    ? (Math.abs(quotedValueDifference) / Math.abs(previousRevision.quotedValue)) * 100
                    : null;

                return {
                    ...revision,
                    label: `${revision.label}${revisionIndex === trail.length - 1 ? " (current)" : ""}`,
                    quotedValueDifference,
                    quotedValueDifferencePercent,
                };
            });
    };

    const toggleRevisionTrail = (event, rowIndex, vendorIndex, trailLength) => {
        event.stopPropagation();
        if (trailLength <= 1) return;

        const key = `${rowIndex}-${vendorIndex}`;
        setOpenRevisionTrail((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const showLabourSplitQuantity = canSelectRates || selectedRatesTab;

    useEffect(() => {
        const newSelectedVendors = {}; // This will store the final selected vendor structure

        rfqData?.labourDetails?.forEach((labourItem, rowIndex) => {
            let minRate = Infinity;
            let minSupplierIndexes = [];
            let bestVendorItem = null;

            sortedSuppliers?.forEach((supplier, vendorIndex) => {
                const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                const vendorItem = findVendorLabourItem(vendorQuotation?.labourItems, labourItem, rowIndex);

                if (vendorItem && vendorItem.labourRate > 0) { // Ignore zero rates
                    if (vendorItem.labourRate < minRate) {
                        minRate = vendorItem.labourRate;
                        minSupplierIndexes = [vendorIndex]; // Reset and store new lowest vendor
                        bestVendorItem = vendorItem; // Store the best vendor item
                    } else if (vendorItem.labourRate === minRate) {
                        minSupplierIndexes.push(vendorIndex); // Add vendor to lowest list
                    }
                }
            });

            // Apply conditions based on selectedRatesTab and isSelected flag
            const selectedSupplierPrice = bestVendorItem ? bestVendorItem.labourRate * labourItem.quantity : 0;

            if (!selectedRatesTab) {
                // If selectedRatesTab is false, select the lowest price vendor
                if (minSupplierIndexes.length > 0) {
                    const selectedSupplierId = sortedSuppliers[minSupplierIndexes[0]].supplierId;

                    // Add the selected labour data to the vendor's entry
                    if (!newSelectedVendors[selectedSupplierId]) {
                        newSelectedVendors[selectedSupplierId] = []; // Initialize an empty array for the vendor
                    }

                    newSelectedVendors[selectedSupplierId].push({
                        labourId: labourItem._id,
                        quantity: labourItem.quantity,
                        selectedQuantity: labourItem.quantity,
                        selectedLabourRate: bestVendorItem?.labourRate ?? 0,
                        selectedSupplierPrice,
                    });
                }
            } else {
                // If selectedRatesTab is true, use isSelected flag
                sortedSuppliers?.forEach((supplier) => {
                    const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                    const vendorItem = findVendorLabourItem(vendorQuotation?.labourItems, labourItem, rowIndex);


                    if (vendorItem?.isSelected) {
                        const selectedVendorId = supplier.supplierId;

                        // Add the selected labour data to the vendor's entry
                        if (!newSelectedVendors[selectedVendorId]) {
                            newSelectedVendors[selectedVendorId] = []; // Initialize an empty array for the vendor
                        }

                        newSelectedVendors[selectedVendorId].push({
                            labourId: labourItem._id,
                            quantity: labourItem.quantity,
                            selectedQuantity: vendorItem.selectedQuantity ?? labourItem.quantity,
                            selectedLabourRate: vendorItem.selectedLabourRate ?? 0,
                            selectedSupplierPrice: vendorItem.selectedSupplierPrice ?? ((vendorItem.selectedLabourRate ?? vendorItem.labourRate ?? 0) * (vendorItem.selectedQuantity ?? labourItem.quantity)),
                        });
                    }
                });
            }
        });

        setSelectedMaterialsMap(newSelectedVendors);
    }, [rfqData, sortedSuppliers, selectedRatesTab, setSelectedMaterialsMap]);


    rfqData?.labourDetails?.forEach((labourItem, rowIndex) => {
        let minRate = Infinity;
        let minSupplierIndexes = [];

        sortedSuppliers?.forEach((supplier, vendorIndex) => {
            const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
            const vendorItem = findVendorLabourItem(vendorQuotation?.labourItems, labourItem, rowIndex);

            if (vendorItem && vendorItem.labourRate > 0) {
                if (vendorItem.labourRate < minRate) {
                    minRate = vendorItem.labourRate;
                    minSupplierIndexes = [vendorIndex];
                } else if (vendorItem.labourRate === minRate) {
                    minSupplierIndexes.push(vendorIndex);
                }
            }
        });

        const lowestRate = minRate === Infinity ? 0 : minRate;
        totalLowestPriceSum += lowestRate * labourItem.quantity;
        lowestRates[labourItem._id] = { minRate, minSupplierIndexes };
    });

    useEffect(() => {
        onTotalLowestLabourCostChange(totalLowestPriceSum);
    }, [totalLowestPriceSum, onTotalLowestLabourCostChange]);

    const handleLabourSelectionChange = (supplierId, labourId, vendorIndex) => {
        if (isLabourSelectionLocked) return;

        setSelectedMaterialsMap(prev => {
            const updatedVendors = Object.entries(prev || {}).reduce((acc, [id, items]) => {
                acc[id] = [...(items || [])];
                return acc;
            }, {});
            const currentSupplierItems = updatedVendors[supplierId] || [];
            const existingSelection = currentSupplierItems.find(item => String(item.labourId) === String(labourId));

            if (existingSelection) {
                updatedVendors[supplierId] = currentSupplierItems.filter(item => String(item.labourId) !== String(labourId));
                if (updatedVendors[supplierId].length === 0) delete updatedVendors[supplierId];
                return updatedVendors;
            }

            // Fetch vendor details to calculate rates and costs
            const rfqLabour = rfqData?.labourDetails?.find(item => String(item?._id) === String(labourId));
            const rfqLabourIndex = rfqData?.labourDetails?.findIndex(item => String(item?._id) === String(labourId));
            const vendorItem = findVendorLabourItem(
                sortedSuppliers[vendorIndex]?.vendorQuotations?.quotationSets?.slice(-1)[0]?.labourItems,
                rfqLabour,
                rfqLabourIndex >= 0 ? rfqLabourIndex : 0
            );
            const selectedQuantity = Math.max(getLabourQuantity(rfqLabour) - getSelectedQuantityForLabour(labourId, supplierId, updatedVendors), 0);
            const selectedLabourRate = vendorItem?.labourRate ?? 0;

            // Construct new labour data to be added
            const existingLabourData = {
                labourId,
                quantity: rfqLabour?.quantity ?? vendorItem?.quantity,
                selectedQuantity,
                selectedLabourRate,
                selectedSupplierPrice: calculateSelectedSupplierPrice(selectedLabourRate, selectedQuantity),
            };

            // Add the new selected labour item to the vendor's entry
            if (supplierId) {
                if (!updatedVendors[supplierId]) {
                    updatedVendors[supplierId] = [];
                }
                updatedVendors[supplierId].push(existingLabourData);
            }

            return updatedVendors;
        });
    };


    const handleLabourRateChange = (supplierId, labourId, value, vendorItem, labourItem) => {
        setSelectedMaterialsMap((prevVendors) => {
            let updatedVendors = Object.entries(prevVendors || {}).reduce((acc, [id, items]) => {
                acc[id] = [...(items || [])];
                return acc;
            }, {});

            // Check if the supplier exists in the selected vendors, if not, initialize it
            if (!updatedVendors[supplierId]) {
                updatedVendors[supplierId] = [];
            }

            // Find the existing labour entry or create a new one
            let labourEntry = updatedVendors[supplierId].find(item => item.labourId === labourId);

            if (!labourEntry) {
                const selectedQuantity = Math.max(getLabourQuantity(labourItem) - getSelectedQuantityForLabour(labourId, supplierId, updatedVendors), 0);
                labourEntry = {
                    labourId,
                    quantity: labourItem?.quantity ?? vendorItem?.quantity,
                    selectedQuantity,
                    selectedLabourRate: vendorItem?.labourRate,
                    selectedSupplierPrice: calculateSelectedSupplierPrice(vendorItem?.labourRate, selectedQuantity),
                };
                updatedVendors[supplierId].push(labourEntry);
            }

            // Update labour rate and cost while preserving other fields
            labourEntry.selectedLabourRate = parseFloat(value) || 0;
            labourEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
                labourEntry.selectedLabourRate,
                getSelectedQuantity(labourEntry, labourItem)
            );

            return updatedVendors;
        });
    };

    const handleLabourQuantityChange = (supplierId, labourId, value, vendorItem, labourItem) => {
        const normalizedValue = normalizeDecimalInput(value);

        setSelectedMaterialsMap((prevVendors) => {
            const updatedVendors = Object.entries(prevVendors || {}).reduce((acc, [id, items]) => {
                acc[id] = [...(items || [])];
                return acc;
            }, {});
            const labourEntry = updatedVendors[supplierId]?.find(item => String(item.labourId) === String(labourId));
            if (!labourEntry) return prevVendors;

            const selectedQuantity = parseNumber(normalizedValue);
            labourEntry.selectedQuantity = normalizedValue;
            labourEntry.quantity = labourItem?.quantity ?? vendorItem?.quantity;
            labourEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
                labourEntry.selectedLabourRate ?? vendorItem?.labourRate,
                selectedQuantity
            );

            return updatedVendors;
        });
    };

    const calculatePercentageDifference = (firstPrice, latestPrice) => {
        if (!firstPrice || firstPrice === 0) return 0;
        return latestPrice - firstPrice;
    };

    const calculateDifferencePercent = (firstPrice, difference) => {
        const numericFirstPrice = Number(firstPrice);
        const numericDifference = Number(difference);

        if (!Number.isFinite(numericFirstPrice) || numericFirstPrice === 0 || !Number.isFinite(numericDifference)) {
            return null;
        }

        return (Math.abs(numericDifference) / Math.abs(numericFirstPrice)) * 100;
    };


    return (<>
        {rfqData?.labourDetails?.map((labourItem, rowIndex) => {
            const lineItemKey = getLineItemKey?.("labour", labourItem, rowIndex) ?? `labour:${labourItem?._id ?? labourItem?.id ?? rowIndex}`;
            if (hiddenLineItemKeys.has(lineItemKey)) return null;
            const lineItemLabel = String(labourItem?.skill || "")
                .replace(/<[^>]*>/g, " ")
                .replace(/&nbsp;/gi, " ")
                .replace(/\s+/g, " ")
                .trim() || `Labour item ${rowIndex + 1}`;

            return (
            <tr key={rowIndex} className='bg-white'>
                <div className='sticky h-full left-0 z-10 bg-white' style={{ minWidth: 480, maxWidth: 480, width: 480, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                    <td className="grid grid-cols-18 h-full gap-1 bg-white w-full py-2 px-[22px] text-xs z-10 sticky left-0 border-gray-300">
                        <div className="col-span-1 flex flex-col items-center gap-1">
                            <span>{rowIndex + 1}.</span>
                            {renderLineItemHideButton?.(lineItemKey, lineItemLabel)}
                        </div>
                        <p
                            onClick={(e) => {
                                if (labourItem?.skill?.length > 200) {
                                    e.stopPropagation(); // IMPORTANT (prevents row click)
                                    togglePopup(labourItem.skill);
                                }
                            }}
                            className='break-words cursor-pointer'
                            style={{ gridColumn: `span ${hasBudgetColumns ? 5 : 12} / span ${hasBudgetColumns ? 5 : 12}` }}>
                            <>
                                <div className="line-clamp-3 text-ellipsis overflow-hidden">
                                    {ReactHtmlParser(labourItem?.skill)}
                                </div>
                                {labourItem?.skill && labourItem?.skill !== "N/A" && labourItem?.skill.length > 200 && (
                                    <button
                                        type="button"
                                        className="text-gray-400 text-xs underline italic"
                                    >
                                        Read More
                                    </button>
                                )}
                            </>
                        </p>
                        <p className='col-span-2'>{labourItem?.quantity} {getFormattedUnit(labourItem?.unit)}</p>
                        {hasBudgetColumns && (
                            <>
                                <p className="text-xs text-right col-span-3">
                                    {labourItem?.budget > 0
                                        ? new Intl.NumberFormat('en-IN', {
                                            maximumFractionDigits: 2,
                                        }).format(labourItem?.budget)
                                        : '-'}
                                </p>
                                <p className="text-xs text-right col-span-4">
                                    {labourItem?.budgetAmount > 0
                                        ? new Intl.NumberFormat('en-IN', {
                                            maximumFractionDigits: 2,
                                        }).format(labourItem?.budgetAmount)
                                        : '-'}
                                </p>
                            </>
                        )}
                        <p className="col-span-3 text-xs text-blue-700 text-right break-words cursor-pointer hover:underline"
                            onClick={() => {
                                const lowestVendorIndex =
                                    lowestRates[labourItem._id]?.minSupplierIndexes?.[0];

                                if (lowestVendorIndex === undefined) return;

                                const cell = vendorCellRefs.current[`${rowIndex}-${lowestVendorIndex}`];
                                if (!cell) return;

                                const scrollContainer = cell.closest(".comparison-scrollbar");
                                if (!scrollContainer) return;

                                const stickyColumn = scrollContainer.querySelector(".fixed-column");
                                const stickyWidth = stickyColumn?.offsetWidth || 420;

                                const cellLeft =
                                    cell.getBoundingClientRect().left -
                                    scrollContainer.getBoundingClientRect().left;

                                const targetScrollLeft =
                                    scrollContainer.scrollLeft + cellLeft - stickyWidth;

                                scrollContainer.scrollTo({
                                    left: targetScrollLeft,
                                    behavior: "smooth",
                                });
                            }}
                        >
                            {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 })
                                .format(lowestRates[labourItem._id]?.minRate ?? 0)}
                        </p>
                    </td>
                </div>

                {sortedSuppliers?.map((supplier, vendorIndex) => {
                    const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                    const firstVendorQuotation = supplier.vendorQuotations?.quotationSets?.[0];
                    const vendorItem = findVendorLabourItem(vendorQuotation?.labourItems, labourItem, rowIndex) || {};
                    const firstVendorItem = findVendorLabourItem(firstVendorQuotation?.labourItems, labourItem, rowIndex) || {};
                    const supplierLabourRate = selectedMaterialsMap[supplier.supplierId]?.some(item => item.labourId === labourItem._id)
                        ? selectedMaterialsMap[supplier.supplierId].find(item => item.labourId === labourItem._id)?.selectedLabourRate
                        : vendorItem.labourRate ?? 0;

                    const latestLabourRate = vendorItem.labourRate ?? 0;
                    const firstLabourRate = firstVendorItem.labourRate ?? 0;
                    const selectedLabourEntry = selectedMaterialsMap[supplier.supplierId]?.find(item => item.labourId === labourItem._id);
                    const quotedValue = withGst
                        ? (vendorItem.totalAmount ?? 0)
                        : (vendorItem.priceWithoutGst ?? 0);
                    const selectedValue = selectedLabourEntry?.selectedSupplierPrice ?? quotedValue;

                    const isLowest = lowestRates[labourItem._id]?.minSupplierIndexes.includes(vendorIndex);
                    const isSelected = !!selectedLabourEntry;
                    const selectedQuantityForLabour = getSelectedQuantityForLabour(labourItem._id);
                    const isQuantityExceeded = selectedQuantityForLabour > getLabourQuantity(labourItem);
                    const percentageDifference = calculatePercentageDifference(firstLabourRate, latestLabourRate);
                    const differencePercent = calculateDifferencePercent(firstLabourRate, percentageDifference);
                    const revisionTrail = getLabourRevisionTrail(supplier, labourItem, rowIndex);
                    const revisionCount = Math.max(revisionTrail.length - 1, 0);
                    const isRevisionTrailOpen = openRevisionTrail.has(`${rowIndex}-${vendorIndex}`);


                    return (
                        <td
                            ref={(el) => {
                                if (el) {
                                    vendorCellRefs.current[`${rowIndex}-${vendorIndex}`] = el;
                                }
                            }}
                            key={vendorIndex}
                            className={`min-w-[600px] px-4 z-0 border border-gray-300 ${vendorIndex < rfqData?.suppliers?.length - 1 ? 'border-r' : ''}`}
                        >
                            <div onClick={() => {
                                /* Legacy labour-details dropdown trigger disabled for now. */
                            }} className='relative flex justify-between items-end py-1'>
                                <div className='w-full'>
                                    {vendorItem.workStartDate && rfqData.providingDate && (
                                        new Date(vendorItem.workStartDate).getTime() !== new Date(rfqData.providingDate).getTime() && (
                                            <p className='text-xs text-[#DC7604]'>
                                                Proposed: {new Date(vendorItem.workStartDate).toLocaleDateString('en-GB')}
                                            </p>
                                        )
                                    )}
                                    <div className='cursor-default'>
                                        <div className={`grid ${
                                            showLabourSplitQuantity
                                                ? (showSelectedValueColumn ? 'grid-cols-6' : 'grid-cols-5')
                                                : (showSelectedValueColumn ? 'grid-cols-5' : 'grid-cols-4')
                                        } gap-x-2`}>
                                            <div className=' col-span-1'>
                                                <div className='flex space-x-1'>
                                                    {canSelectRates && <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => handleLabourSelectionChange(supplier.supplierId, labourItem._id, vendorIndex)}
                                                        disabled={isLabourSelectionLocked}
                                                        className="h-3 w-3 focus:outline-none border-solid bg-gray-100 focus:ring-0 cursor-pointer accent-[#DC7604]"
                                                    />}

                                                    {/* Price Information */}
                                                    <p className={`text-xs text-[#777777] ${isLowest ? 'bg-lime-200 px-1 w-fit' : ''}`}>
                                                        {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(supplierLabourRate ?? 0)}/qty.
                                                    </p>
                                                </div>
                                                {!isLabourSelectionLocked && canSelectRates && (
                                                    <input
                                                        type="text"
                                                        value={
                                                            selectedMaterialsMap[supplier.supplierId]?.some(item => item.labourId === labourItem._id)
                                                                ? selectedMaterialsMap[supplier.supplierId].find(item => item.labourId === labourItem._id)?.selectedLabourRate || ''
                                                                : vendorItem?.labourRate || ''
                                                        }
                                                        onChange={(e) => handleLabourRateChange(supplier.supplierId, labourItem._id, e.target.value, vendorItem, labourItem)}
                                                        disabled={!selectedMaterialsMap[supplier.supplierId]?.some(item => item.labourId === labourItem._id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="w-[70px] px-1 py-0 mt-2 col-span-1 text-xs border rounded-md"
                                                    />
                                                )}
                                            </div>

                                            {showLabourSplitQuantity && <div className="col-span-1 text-right">
                                                {canSelectRates && isSelected && !isLabourSelectionLocked ? (
                                                    <input
                                                        type="text"
                                                        value={
                                                            selectedMaterialsMap[supplier.supplierId]?.find(item => item.labourId === labourItem._id)?.selectedQuantity ?? ''
                                                        }
                                                        onChange={(e) => handleLabourQuantityChange(supplier.supplierId, labourItem._id, e.target.value, vendorItem, labourItem)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`w-[64px] px-1 py-0 text-xs border rounded-md text-right ${isQuantityExceeded ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                                                    />
                                                ) : (
                                                    <p className="text-xs text-right">
                                                        {isSelected
                                                            ? getSelectedQuantity(selectedMaterialsMap[supplier.supplierId]?.find(item => item.labourId === labourItem._id), labourItem)
                                                            : '-'}
                                                    </p>
                                                )}
                                            </div>}

                                            <p className='text-xs text-right col-span-1'>{firstLabourRate > 0
                                                ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(firstLabourRate)
                                                : '-'}
                                            </p>
                                            <div className={`text-xs text-right col-span-1 flex flex-col items-end ${parseFloat(percentageDifference) < 0
                                                ? 'text-green-600'
                                                : parseFloat(percentageDifference) > 0
                                                    ? 'text-red-600'
                                                    : 'text-[#777777]'
                                                }`}
                                            >
                                                <span>
                                                    {Math.abs(parseFloat(percentageDifference)).toFixed(2)} {" "}
                                                    {parseFloat(percentageDifference) < 0 && '\u25BC'}
                                                    {parseFloat(percentageDifference) > 0 && '\u25B2'}
                                                </span>
                                                <span className="text-[10px] leading-3">
                                                    {differencePercent === null ? '-' : '(' + differencePercent.toFixed(2) + '%)'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col items-end text-right">
                                                <p className="text-xs font-normal text-[#2D2D11]">
                                                    {selectedMaterialsMap?.[supplier.supplierId]?.some(item => item.labourId === labourItem._id)
                                                        ? new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(
                                                            selectedMaterialsMap[supplier.supplierId].find(item => item.labourId === labourItem._id)?.selectedSupplierPrice ?? 0
                                                        )
                                                        : (withGst
                                                            ? new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(vendorItem.totalAmount ?? 0)
                                                            : new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0 }).format(vendorItem?.priceWithoutGst ?? 0)
                                                        )
                                                    }
                                                </p>
                                                {revisionTrail.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="mt-[2px] inline-flex items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-[1px] text-[10px] font-semibold leading-4 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                                                        onClick={(event) => toggleRevisionTrail(event, rowIndex, vendorIndex, revisionTrail.length)}
                                                    >
                                                        {isRevisionTrailOpen
                                                            ? "Hide revisions"
                                                            : `${revisionCount} ${revisionCount === 1 ? "revision" : "revisions"}`}
                                                    </button>
                                                )}
                                            </div>
                                            {showSelectedValueColumn && (
                                                <p className="text-xs text-right font-normal text-[#2D2D11]">
                                                    {isSelected
                                                        ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(selectedValue)
                                                        : '-'}
                                                </p>
                                            )}
                                        </div>

                                        {isRevisionTrailOpen && (
                                            <div
                                                className="mt-2 w-[calc(100%_+_24px)] min-w-[520px] rounded-xl border border-blue-100 bg-[#F8FAFC] px-3 py-3 text-left text-[11px] text-[#3F4B64] shadow-lg"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <div className="grid grid-cols-[38px_108px_64px_54px_44px_72px_minmax(100px,1fr)] gap-x-2 border-b border-gray-400 pb-2 text-[11px] font-semibold text-gray-500">
                                                    <span>Round</span>
                                                    <span className="text-center">Date</span>
                                                    <span className="text-right">Rate</span>
                                                    <span className="text-right">Qty</span>
                                                    <span className="text-right">GST</span>
                                                    <span className="text-right">Difference</span>
                                                    <span className="text-right">Quoted value</span>
                                                </div>
                                                {revisionTrail.map((revision, revisionIndex) => (
                                                    <React.Fragment key={`${revision.label}-${revisionIndex}`}>
                                                        <div
                                                            className={`grid grid-cols-[38px_108px_64px_54px_44px_72px_minmax(100px,1fr)] gap-x-2 border-b ${revision.remark ? "border-gray-200" : "border-gray-300"} py-2`}
                                                        >
                                                            <span className={`font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>{revision.label}</span>
                                                            <span className="whitespace-normal break-words text-center leading-4">
                                                                <span className="block">{revision.submittedBy}</span>
                                                                <span className="block">{revision.date}</span>
                                                            </span>
                                                            <span className="text-right font-medium text-[#2D2D11]">{formatRevisionAmount(revision.labourRate)}</span>
                                                            <span className="text-right font-medium text-[#2D2D11]">{formatRevisionAmount(revision.quantity)}</span>
                                                            <span className="text-right font-medium text-[#2D2D11]">{formatRevisionPercent(revision.gst)}</span>
                                                            <span className={`text-right font-semibold ${revision.quotedValueDifference < 0 ? "text-emerald-600" : revision.quotedValueDifference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                                                                {formatRevisionDifference(revision.quotedValueDifference, revision.quotedValueDifferencePercent)}
                                                            </span>
                                                            <span className={`text-right font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                                                                {formatRevisionAmount(revision.quotedValue)}
                                                            </span>
                                                        </div>
                                                        {revision?.remark && (
                                                            <div className="border-b border-gray-300 py-1 text-gray-600">
                                                                <span className="font-semibold">Remarks:</span>{" "}{revision.remark}
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        )}


                                        {withGst && <p className='text-xs text-[#777777]'> GST: {vendorItem?.gst} %</p>}
                                        <div className='flex space-x-4 items-center'>
                                            {vendorItem?.attachments?.length > 0 && <div className='relative'>
                                                <img src="https://storagereponeevaydevcdn.blob.core.windows.net/business/files.svg" alt="" className='w-3 h-3 ' />
                                                <div className='bg-[#f05252] rounded-full -top-2 -right-2 h-3 w-3 flex justify-center items-center  absolute'>
                                                    <p className='text-[10px] text-white text-center pt-[3px]'>{vendorItem?.attachments?.length}</p>
                                                </div>
                                            </div>}


                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                    );
                })}
            </tr>
            );
        })}
        {isPopupOpen && selectedDetail && (
            <StaticPopupNew
                openModal={isPopupOpen}
                handleClose={() => setIsPopupOpen(false)}
                width={"lg"}
                height={80}
                bodyStyle="pb-0 pt-0 px-0"
                className="px-0 text-sm"
                header={"Work Details"}
            >
                <div className="p-4 max-h-[200px] overflow-y-auto">
                    <p className="text-xs text-[#3D3D3D]">
                        {ReactHtmlParser(selectedDetail)}
                    </p>
                </div>
            </StaticPopupNew>
        )}
    </>
    );
};

export default LabourRFQTable;
